import { FunctionComponent, useState } from 'react'
import { Role } from '@/types/Role'
import styles from './childTable.module.scss'
import Table from '@mui/material/Table'
import { TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import Title from '@/components/_custom/Title/title'
import { useTranslation } from 'react-i18next'
import ChildBodyCell from './childBodyCell'
import Desktop from '@/modules/desktopMobile/desktop'
import { Child, ChildDaycare } from '@/types/Child'
import { useAuth } from '@/modules/context/AuthContext'
import { ParentDaycare } from '@/types/Parent'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptions } from '@/types/Select'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Add, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import AddChildToParentDialog from '../Dialog/addChildToParentDialog'

interface ChildTableProps {
    daycare?: ParentDaycare
    parentId: number
    childs: Child[]
    setChilds: (value: Child[]) => void
    isNanny: boolean
    sections: SelectOptions<number>
    groups: SelectOptions<number>
    selectOptionsDaycare: SelectOptions<number>
    handleSetSelectedDaycare: (value: number) => void
    isRpeOrLaep: boolean
    handleRefetchParent: () => Promise<void>
    parentName: string
    haveDaycare: boolean
}

const TableChild: FunctionComponent<ChildTableProps> = ({
    daycare,
    parentId,
    childs,
    isNanny,
    setChilds,
    sections,
    groups,
    selectOptionsDaycare,
    handleSetSelectedDaycare,
    isRpeOrLaep,
    handleRefetchParent,
    parentName,
    haveDaycare
}: ChildTableProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const [addChildToParentDialog, setAddChildToParentDialog] = useState(false)

    const [showMore, setShowMore] = useState<boolean>(true)

    function deleteChild(childId: number) {
        const newChilds: Child[] = childs.filter((child: Child): boolean => child.id !== childId)
        setChilds([])
        setChilds(newChilds)
    }

    return (
        user && (
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <div className={styles.Title} onClick={() => setShowMore(!showMore)}>
                        <Title text={t('childs')}></Title>
                        {showMore ? (
                            <KeyboardArrowUp className={styles.Icon} />
                        ) : (
                            <KeyboardArrowDown className={styles.Icon} />
                        )}
                    </div>
                    {!(isNanny && isRpeOrLaep) && daycare && selectOptionsDaycare[0].options.length > 1 && (
                        <div className={styles.Input}>
                            <SelectUnique
                                selectOptions={selectOptionsDaycare}
                                selectValue={daycare.daycareId}
                                setSelectValue={handleSetSelectedDaycare}
                                label={t('structure')}
                                className={styles.UserInput}
                            />
                        </div>
                    )}
                </div>
                {showMore && (
                    <>
                        {childs.length > 0 ? (
                            <TableContainer className={styles.TablePlacement}>
                                <Table className={styles.Table}>
                                    <TableHead className={styles.TableHeader}>
                                        <TableRow>
                                            {((daycare && haveDaycare) || user.role === Role.cald) && (
                                                <Desktop>
                                                    <TableCell sx={{ width: '5%' }} />
                                                </Desktop>
                                            )}
                                            <TableCell>{t('firstname')}</TableCell>
                                            <Desktop>
                                                <TableCell>{t('birthdate')}</TableCell>
                                            </Desktop>
                                            {daycare && haveDaycare && (
                                                <Desktop>
                                                    {user.currentDaycareRights.canReadChild &&
                                                        user.role !== Role.parent &&
                                                        !(isNanny && daycare.isRpeOrLaep) && (
                                                            <TableCell>
                                                                {daycare.isRpeOrLaep ? t('sector') : t('section')}
                                                            </TableCell>
                                                        )}
                                                    {user.currentDaycareRights.canReadOccasionalSlot &&
                                                        user.role !== Role.parent && (
                                                            <TableCell align={'center'}>{t('request')}</TableCell>
                                                        )}
                                                    {daycare && (
                                                        <TableCell align={'center'}>{t('visibility')}</TableCell>
                                                    )}
                                                </Desktop>
                                            )}
                                            {(haveDaycare || user.role === Role.cald) && (
                                                <TableCell align={'center'}>{t('actions')}</TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    {[...childs]
                                        .sort((childA: Child, childB: Child) => {
                                            const daycareA: ChildDaycare | undefined = childA.daycares.find(
                                                (d: ChildDaycare) => d.daycareId === daycare?.daycareId
                                            )
                                            const daycareB: ChildDaycare | undefined = childB.daycares.find(
                                                (d: ChildDaycare) => d.daycareId === daycare?.daycareId
                                            )

                                            if (!daycareA || !daycareB) {
                                                return childA.firstname.localeCompare(childB.firstname)
                                            }

                                            if (daycareA.visible !== daycareB.visible) {
                                                return daycareA.visible ? -1 : 1
                                            }

                                            if (daycareA.sections.length !== daycareB.sections.length) {
                                                return daycareA.sections.length > daycareB.sections.length ? -1 : 1
                                            }

                                            return childA.firstname.localeCompare(childB.firstname)
                                        })
                                        .map((child, value) => (
                                            <TableBody key={value}>
                                                <ChildBodyCell
                                                    child={child}
                                                    daycare={daycare}
                                                    sections={sections.flatMap((section) => section.options)}
                                                    groups={groups.flatMap((group) => group.options)}
                                                    deleteChild={deleteChild}
                                                    isNanny={isNanny}
                                                    isRpeOrLaep={isRpeOrLaep}
                                                    handleRefetchParent={handleRefetchParent}
                                                    parentId={parentId}
                                                    haveDaycare={haveDaycare}
                                                />
                                            </TableBody>
                                        ))}
                                </Table>
                            </TableContainer>
                        ) : (
                            <div className={styles.NoChilds}>{t('noChilds')}</div>
                        )}
                        {user.currentDaycareRights.canWriteFamilies && haveDaycare && daycare && (
                            <>
                                <div className={styles.ButtonContainer}>
                                    <ButtonTextIcon
                                        type={'custom'}
                                        customText={t('addChild')}
                                        customIcon={<Add />}
                                        className={styles.Button}
                                        disabled={!user.currentDaycareRights.canWriteFamilies}
                                        onClick={() => setAddChildToParentDialog(true)}
                                    />
                                </div>
                                {addChildToParentDialog && (
                                    <AddChildToParentDialog
                                        open={addChildToParentDialog}
                                        handleClose={() => setAddChildToParentDialog(false)}
                                        parentId={parentId}
                                        daycareId={daycare.daycareId}
                                        parentName={parentName}
                                        handleRefetchParent={handleRefetchParent}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        )
    )
}

export default TableChild
